import { isSystemFile, normaliseSourcePath } from "@gb/contracts";

/**
 * The folder each file was filed in, as the browser can tell it (2026-09-26,
 * LIN-01, `document-ai/0015` §8).
 *
 * The owner decided on 2026-09-18 that a file's folder is a filing fact the
 * steps may read. Nothing collected it, so every uploaded file arrived with
 * none, and "00_OLD", "A Template" and "08 Subcontractors/02 Contract" never
 * reached a line. The path starts at the folder a person picked: the folders
 * above it are not the browser's to give, which also keeps the names of the
 * people whose folders those are away from the server.
 *
 * A dropped folder was walked here too, for setup's Documents step. The step
 * went on 2026-10-03, and the owner chose the same day that Brain → Sources
 * keeps its file picker alone, so the walk went with it (`product/0016` §4).
 * The next day the owner asked for a whole folder to be uploadable from
 * Sources, so the folder picker came back beside the file picker
 * (`pickedFolder`); dropping a folder did not.
 */

/** A file a person picked, and the folder it was in when the browser said. */
export type PickedFile = { file: File; sourcePath: string | null };

/**
 * The folder of a file from a folder picker (`<input webkitdirectory>`): its
 * `webkitRelativePath` less its own name. Null for a file picked on its own,
 * whose path the browser leaves empty.
 */
export function folderOf(file: File): string | null {
  const relative = (file as File & { webkitRelativePath?: string }).webkitRelativePath ?? "";
  const cut = relative.lastIndexOf("/");
  return cut > 0 ? normaliseSourcePath(relative.slice(0, cut), file.name) : null;
}

/**
 * The files of a folder a person picked, each with its folder, less the
 * operating system's own files (2026-10-04). A Mac folder carries a
 * `.DS_Store` in every subfolder. Those are never documents (`document-ai/0015`
 * §8), so they are left out here rather than listed as refused uploads, one
 * row each; the rule is the contract's own (`isSystemFile`). A system file a
 * person picks on its own still gets its sentence.
 */
export function pickedFolder(files: readonly File[]): PickedFile[] {
  return files.filter((file) => !isSystemFile(file.name)).map((file) => ({ file, sourcePath: folderOf(file) }));
}

/** Each file with its folder, whether it came with one or a picker gave it one. */
export function withFolders(files: readonly (File | PickedFile)[]): PickedFile[] {
  return files.map((item) => (item instanceof File ? { file: item, sourcePath: folderOf(item) } : item));
}
